import React, { useCallback, useState, useMemo, useEffect } from 'react';
import {
  parseInputChange,
  formatTimeForDisplay
} from 'simple-time-input-engine';
import TextField from '@mui/material/TextField';
import { DateTime } from 'luxon';
import { InputAdornment, PopoverOrigin } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import StartTimeIcon from '@mui/icons-material/Timer';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import flow from 'lodash/flow';
import { addSpace, formatTimeforOption } from 'utils/duration-util';
import { useMessage } from 'modules/intl';
import { useFeatureFlag } from 'modules/common/hooks';

const useStyles = makeStyles((theme) => ({
  menuPaper: {
    maxHeight: theme.spacing(31.5),
    marginTop: theme.spacing(1.2),
    width: '17.7ch'
  },
  icon: {
    padding: theme.spacing(0)
  }
}));

const useGetClockOption = (clockMode: 12 | 24) => {
  return useMemo(() => {
    const res = Array.from(new Array(48)).map((_, index) => {
      const seconds = 1800 * index;
      return formatTimeforOption(seconds, clockMode);
    });

    return res;
  }, [clockMode]);
};

const useGetCurrentTimeOption = (time: DateTime, clockMode: 12 | 24) => {
  return useMemo(() => {
    let seconds = time.hour * 60 * 60 + time.minute * 60 + time.second;
    seconds = seconds - (seconds % 1800) + 1800;
    return formatTimeforOption(seconds, clockMode);
  }, [clockMode, time]);
};

type TimeInputFieldProps = {
  clockMode: 12 | 24;
  value?: DateTime | null;
  onChange: (val: DateTime | null) => void;
  name: string;
  testId: string;
  label: string;
  isEndTime?: boolean;
  className?: string | undefined;
  icon?: React.ReactNode;
  disabled?: boolean;
  setResetTimeValue?: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  suggestionProps?: {
    setHasSuggestionStartTime: React.Dispatch<React.SetStateAction<boolean>>;
    setHasSuggestionDuration: React.Dispatch<React.SetStateAction<boolean>>;
    setHasSuggestionEndTime: React.Dispatch<React.SetStateAction<boolean>>;
    hasSuggestionDuration: boolean;
  };
  iconLabel?: string | undefined;
};

const useTimeErrorProps = (isInvalid: boolean, errorText: string) => {
  const timeErrorProps = useMemo(
    () =>
      isInvalid
        ? {
            error: true,
            helperText: errorText
          }
        : {},
    [isInvalid, errorText]
  );

  return timeErrorProps;
};

const formatTime = (time?: DateTime | null) => {
  if (time && time.isValid) {
    return time.toLocaleString({
      hour: 'numeric',
      minute: 'numeric',
      hourCycle: 'h23'
    } as any);
  } else return '';
};

const formatTimeForDisplayModified: ({
  time,
  clockMode
}: {
  time: string;
  clockMode: number;
}) => string = flow([formatTimeForDisplay, addSpace]);

const anchorOrigin: PopoverOrigin = {
  vertical: 'bottom',
  horizontal: 37
};
const transformOrigin: PopoverOrigin = {
  vertical: 'top',
  horizontal: 'right'
};
export const TimeInputField: React.FC<TimeInputFieldProps> = ({
  value,
  onChange,
  clockMode,
  name,
  testId,
  isEndTime,
  label,
  disabled = false,
  icon = <StartTimeIcon color="action" />,
  setResetTimeValue,
  suggestionProps,
  iconLabel,
  ...inputProps
}: TimeInputFieldProps) => {
  const classes = useStyles();
  const timeValue = formatTime(value);
  const [time, setTime] = useState<string>(timeValue);
  const [inputValue, setInputValue] = useState<string>(
    formatTimeForDisplayModified({ time: timeValue, clockMode })
  );

  const checkValid = value ? value.isValid : true;
  const [isInvalid, setIsInvalid] = useState<boolean>(!checkValid);
  const errorMessages = useMessage('note.error.invalidTimeFormat') || '';
  const timeErrorProps = useTimeErrorProps(isInvalid, errorMessages);
  useEffect(() => {
    const err = value === null ? false : !value?.isValid;
    if (!err) {
      const timeValue = formatTime(value);
      setTime(timeValue);
      setInputValue(
        formatTimeForDisplayModified({ time: timeValue, clockMode })
      );
      setIsInvalid(false);
    }
  }, [clockMode, value]);

  const updateTimeBasedOnInput = useCallback(
    (event) => {
      const { valid, time: newTime } = parseInputChange({
        newValue: event.target.value.replace('.', ':'),
        previousTime: time,
        clockMode
      });
      if (time === newTime) {
        setTime(newTime);
        setInputValue(
          formatTimeForDisplayModified({ time: newTime, clockMode })
        );
        return;
      }
      if (valid) {
        setTime(newTime);
        setInputValue(
          formatTimeForDisplayModified({ time: newTime, clockMode })
        );
      }

      setIsInvalid(!valid);

      let luxonDate: DateTime | null;
      if (valid) {
        if (newTime) {
          luxonDate = value?.isValid
            ? value.set({
                hour: parseInt(newTime.split(':')[0]),
                minute: parseInt(newTime.split(':')[1])
              })
            : DateTime.fromISO(newTime);
          suggestionProps?.setHasSuggestionStartTime(false);

          suggestionProps?.setHasSuggestionDuration(false);

          suggestionProps?.setHasSuggestionEndTime(false);
        } else {
          luxonDate = null;
        }
      } else {
        luxonDate = DateTime.invalid('Invalid format');
      }

      luxonDate = luxonDate
        ? luxonDate.set({ second: 0, millisecond: 0 })
        : null;

      if (onChange) {
        onChange(luxonDate);
      }
    },
    [suggestionProps, clockMode, onChange, time, value]
  );

  const onInputChange = useCallback(
    (event) => {
      const { value } = event.target;
      setInputValue(value);
      const val = value.replace('.', ':');
      const { valid } = parseInputChange({
        newValue: val,
        previousTime: '',
        clockMode
      });
      setIsInvalid(!valid);

      setResetTimeValue ? setResetTimeValue(false) : undefined;
    },
    [clockMode, setResetTimeValue]
  );

  //options
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const options = useGetClockOption(clockMode);
  const currentFormattedTime = useGetCurrentTimeOption(
    value ?? DateTime.local(),
    clockMode
  );
  const currentFormattedTimeEnd = useGetCurrentTimeOption(
    value ?? DateTime.local().plus({ minutes: 30 }),
    clockMode
  );

  const handleClick = useCallback(
    (event: any) => {
      setAnchorEl(event.currentTarget);
      setResetTimeValue ? setResetTimeValue(false) : undefined;
    },
    [setResetTimeValue]
  );

  const handleClose = useCallback(
    (option: any) => {
      if (typeof option === 'string') {
        setInputValue(option);
        setIsInvalid(false);

        const { time: newTime } = parseInputChange({
          newValue: option,
          previousTime: time,
          clockMode
        });

        setTime(newTime);

        let luxonDate = value?.isValid
          ? value.set({
              hour: parseInt(newTime.split(':')[0]),
              minute: parseInt(newTime.split(':')[1])
            })
          : DateTime.fromISO(newTime);

        luxonDate = luxonDate.set({ second: 0, millisecond: 0 });

        if (onChange) {
          onChange(luxonDate);
        }
        suggestionProps?.setHasSuggestionStartTime(false);

        suggestionProps?.setHasSuggestionDuration(false);

        suggestionProps?.setHasSuggestionEndTime(false);
      }

      setResetTimeValue ? setResetTimeValue(false) : undefined;

      setAnchorEl(null);
    },
    [suggestionProps, clockMode, onChange, setResetTimeValue, time, value]
  );

  const handleFocus = useCallback((event: any) => {
    event.target.select();
  }, []);

  const menuClasses = useMemo(
    () => ({
      paper: classes.menuPaper
    }),
    [classes.menuPaper]
  );

  const isTiwScreenReaderEnabled = useFeatureFlag('isTiwScreenReaderEnabled');

  const overrideInputProps = useMemo(
    () => ({
      endAdornment: (
        <InputAdornment position="end">
          <IconButton
            data-testid={'input-time-icon-button'}
            onClick={handleClick}
            disabled={disabled}
            className={classes.icon}
            size="small"
            aria-label={isTiwScreenReaderEnabled ? iconLabel : undefined}
          >
            {icon}
          </IconButton>
          {open && (
            <Menu
              data-testid="long-menu-time-option"
              anchorEl={anchorEl}
              keepMounted
              open={open}
              onClose={handleClose}
              classes={menuClasses}
              anchorOrigin={anchorOrigin}
              transformOrigin={transformOrigin}
            >
              {options.map((option) => (
                <MenuItem
                  data-testid={option}
                  key={option}
                  onClick={() => handleClose(option)}
                  selected={
                    isEndTime
                      ? option === currentFormattedTimeEnd
                      : option === currentFormattedTime
                  }
                >
                  {option}
                </MenuItem>
              ))}
            </Menu>
          )}
        </InputAdornment>
      )
    }),
    [
      handleClick,
      disabled,
      classes.icon,
      isTiwScreenReaderEnabled,
      iconLabel,
      icon,
      open,
      anchorEl,
      handleClose,
      menuClasses,
      options,
      isEndTime,
      currentFormattedTimeEnd,
      currentFormattedTime
    ]
  );

  return (
    <TextField
      label={label}
      name={name}
      onBlur={updateTimeBasedOnInput}
      onChange={onInputChange}
      onFocus={handleFocus}
      value={inputValue}
      variant={'outlined'}
      disabled={disabled}
      data-testid={testId}
      InputProps={overrideInputProps}
      size="small"
      {...timeErrorProps}
      {...inputProps}
    />
  );
};
